/**
 * Your data — export and erasure (M1).
 *
 * Reached at `#account`, and linked from the footer once you are signed in.
 * Not in the journey nav: it is a place you go deliberately, not a step.
 *
 * Two design choices worth keeping:
 *
 *   1. The export button says what the file will contain BEFORE you press it,
 *      including the fact that no résumé is in there. "What do you have on me"
 *      is the actual question behind an export request, and answering it in the
 *      UI is faster than making someone read the JSON to find out.
 *
 *   2. Deletion requires typing the word, not ticking a box. It is irreversible
 *      and there is no undo, no soft-delete and no support inbox to write to.
 */
import { useState, useEffect } from "react";
import { exportAccount, downloadJSON, deleteEverything } from "../lib/account";
import { loadHistory } from "../lib/profile";
import { recordSignal, EVENTS } from "../lib/signals";
import { PANEL, TEAL, CREAM, CREAM60, CREAM30, BORDER, AMBER, NAVY } from "../utils/constants";

const CONFIRM_WORD = "DELETE";

export default function Account({ user, onBack }) {
  const [count, setCount] = useState(null);
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);
  const [typed, setTyped] = useState("");

  useEffect(() => {
    let dead = false;
    if (user?.uid) loadHistory(user.uid, 100).then((h) => { if (!dead) setCount(h.length); });
    return () => { dead = true; };
  }, [user]);

  if (!user) {
    return (
      <Shell onBack={onBack}>
        <p style={{ color: CREAM60, fontSize: 14 }}>Sign in to see or export your data.</p>
      </Shell>
    );
  }

  const doExport = async () => {
    setBusy("export"); setMsg(null);
    const res = await exportAccount(user.uid);
    setBusy(null);
    if (!res.ok) return setMsg({ bad: true, text: `Export failed (${res.reason}). Nothing was changed.` });
    downloadJSON(res.data, `signalpath-export-${new Date().toISOString().slice(0, 10)}.json`);
    recordSignal(EVENTS.ACCOUNT_EXPORTED);
    setMsg({ text: `Downloaded — ${res.data.assessments.length} assessment(s).` });
  };

  const doDelete = async () => {
    setBusy("delete"); setMsg(null);
    const res = await deleteEverything(user.uid);
    setBusy(null);
    if (res.ok) {
      // Recorded before the message, and carrying nothing but the event: a
      // deletion rate is a trust signal worth watching, and the signals
      // collection has no identity in it to orphan (ADR-038).
      recordSignal(EVENTS.ACCOUNT_DELETED);
      return setMsg({ text: "Deleted. Everything is gone." });
    }
    if (res.needsReauth) {
      return setMsg({
        bad: true,
        text: `Firebase needs a fresh sign-in before it will delete an account. ` +
              `Sign out, sign back in, and return here. ` +
              (res.deleted ? `${res.deleted} assessment(s) were already removed.` : ""),
      });
    }
    setMsg({ bad: true, text: `Deletion failed (${res.reason}).` });
  };

  return (
    <Shell onBack={onBack}>
      <Row label="Signed in as" value={user.email || user.uid} />
      <Row label="Assessments stored" value={count == null ? "…" : String(count)} />

      <Block title="Export everything">
        <p style={{ color: CREAM60, fontSize: 13, lineHeight: 1.6, margin: "0 0 12px" }}>
          A JSON file with your profile and every dated assessment. Your résumé is not
          in it — it is parsed in your browser and never uploaded, so we do not have a
          copy to give you.
        </p>
        <button onClick={doExport} disabled={busy === "export"}
          style={{ background: TEAL, color: NAVY, border: "none", padding: "11px 20px",
                   borderRadius: 8, cursor: busy ? "default" : "pointer", fontSize: 14,
                   fontWeight: 700, opacity: busy === "export" ? 0.6 : 1 }}>
          {busy === "export" ? "Preparing…" : "Download my data"}
        </button>
      </Block>

      <Block title="Delete everything" tone={AMBER}>
        <p style={{ color: CREAM60, fontSize: 13, lineHeight: 1.6, margin: "0 0 12px" }}>
          Removes every assessment, your profile, and the account itself. This cannot be
          undone, and there is no backup to restore from. Export first if you want a copy.
        </p>
        <label style={{ display: "block", fontSize: 12, color: CREAM30, marginBottom: 6 }}>
          Type <strong style={{ color: AMBER }}>{CONFIRM_WORD}</strong> to confirm
        </label>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input value={typed} onChange={(e) => setTyped(e.target.value)}
            aria-label={`Type ${CONFIRM_WORD} to confirm deletion`}
            style={{ background: "transparent", border: `1px solid ${CREAM30}`, color: CREAM,
                     borderRadius: 8, padding: "10px 12px", fontSize: 14, minWidth: 160,
                     fontFamily: "'JetBrains Mono', monospace" }} />
          <button onClick={doDelete} disabled={typed !== CONFIRM_WORD || busy === "delete"}
            style={{ background: typed === CONFIRM_WORD ? AMBER : "transparent",
                     color: typed === CONFIRM_WORD ? NAVY : CREAM30,
                     border: `1px solid ${typed === CONFIRM_WORD ? AMBER : BORDER}`,
                     padding: "11px 20px", borderRadius: 8, fontSize: 14, fontWeight: 700,
                     cursor: typed === CONFIRM_WORD && !busy ? "pointer" : "default" }}>
            {busy === "delete" ? "Deleting…" : "Delete my account"}
          </button>
        </div>
      </Block>

      {msg && (
        <div role="status" style={{ marginTop: 16, fontSize: 13, lineHeight: 1.6,
                                    color: msg.bad ? AMBER : TEAL }}>
          {msg.text}
        </div>
      )}
    </Shell>
  );
}

function Shell({ children, onBack }) {
  return (
    <div style={{ animation: "fadeUp 0.4s ease" }}>
      <button onClick={onBack}
        style={{ background: "none", border: `1px solid ${CREAM30}`, color: CREAM60,
                 padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontSize: 13,
                 marginBottom: 18 }}>
        ← Back
      </button>
      <h2 style={{ color: CREAM, fontSize: 22, fontWeight: 700, margin: "0 0 4px" }}>Your data</h2>
      <p style={{ color: CREAM30, fontSize: 13, margin: "0 0 22px" }}>
        What SignalPath holds for you, and how to take it or end it.
      </p>
      {children}
    </div>
  );
}

const Row = ({ label, value }) => (
  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "10px 0",
                borderBottom: `1px solid ${BORDER}`, fontSize: 13 }}>
    <span style={{ color: CREAM30 }}>{label}</span>
    <span style={{ color: CREAM, fontFamily: "'JetBrains Mono', monospace" }}>{value}</span>
  </div>
);

const Block = ({ title, children, tone = TEAL }) => (
  <div style={{ background: PANEL, border: `1px solid ${BORDER}`, borderRadius: 12,
                padding: 18, marginTop: 18 }}>
    <div style={{ fontSize: 12, color: tone, fontWeight: 700, textTransform: "uppercase",
                  letterSpacing: 1, marginBottom: 10 }}>{title}</div>
    {children}
  </div>
);
